import { isAxiosError } from 'axios';
import apiClient from './apiClient';

const API_URL = '/points';

export interface PointsData {
  points: number;
  reputationScore: number;
  badges: string[];
}

export interface PointsHistoryEntry {
  _id: string;
  userID: string;
  pointsChange: number;
  reason: string;
  relatedID?: string;
  createdAt: string;
}

export interface PaginatedPointsHistory {
  count: number;
  pagination: {
    total: number;
    page: number;
    pages: number;
  };
  data: PointsHistoryEntry[];
}

// Custom error class for points service
export class PointsServiceError extends Error {
  constructor(
    message: string,
    public originalError?: any
  ) {
    super(message);
    this.name = 'PointsServiceError';
  }
}

// Response validation functions
const validatePointsHistoryResponse = (response: any): PaginatedPointsHistory => {
  if (!response || typeof response !== 'object') {
    throw new PointsServiceError('Invalid response format from server');
  }

  // Validate data array
  let data: PointsHistoryEntry[] = [];
  if (Array.isArray(response.data)) {
    data = response.data.filter((entry: any) => {
      return (
        entry &&
        typeof entry === 'object' &&
        entry._id &&
        typeof entry.pointsChange === 'number' &&
        typeof entry.reason === 'string' &&
        entry.createdAt
      );
    });
  }

  // Validate pagination
  const pagination = {
    total: typeof response.pagination?.total === 'number' ? response.pagination.total : 0,
    page: typeof response.pagination?.page === 'number' ? response.pagination.page : 1,
    pages: typeof response.pagination?.pages === 'number' ? response.pagination.pages : 1,
  };

  return {
    count: typeof response.count === 'number' ? response.count : data.length,
    pagination,
    data,
  };
};

const validatePointsDataResponse = (response: any): PointsData => {
  if (!response || typeof response !== 'object') {
    throw new PointsServiceError('Invalid points data response format');
  }

  return {
    points: typeof response.points === 'number' ? response.points : 0,
    reputationScore: typeof response.reputationScore === 'number' ? response.reputationScore : 0,
    badges: Array.isArray(response.badges) ? response.badges : [],
  };
};

export const getUserPoints = async (): Promise<PointsData> => {
  try {
    const response = await apiClient.get(`${API_URL}`);

    if (!response.data || !response.data.data) {
      throw new PointsServiceError('Invalid response structure from server');
    }

    return validatePointsDataResponse(response.data.data);
  } catch (error) {
    console.error('Error fetching user points:', error);

    if (error instanceof PointsServiceError) {
      throw error;
    }

    if (isAxiosError(error)) {
      const message = error.response?.data?.error || error.message || 'Failed to fetch user points';
      throw new PointsServiceError(message, error);
    }

    throw new PointsServiceError('An unexpected error occurred while fetching user points', error);
  }
};

export const getPointsHistory = async (page = 1, limit = 10): Promise<PaginatedPointsHistory> => {
  try {
    // Validate input parameters
    const validPage = Math.max(1, Math.floor(page));
    const validLimit = Math.max(1, Math.min(100, Math.floor(limit))); // Limit between 1-100

    const response = await apiClient.get(
      `${API_URL}/history?page=${validPage}&limit=${validLimit}`
    );

    if (!response.data) {
      throw new PointsServiceError('Empty response from server');
    }

    return validatePointsHistoryResponse(response.data);
  } catch (error) {
    console.error('Error fetching points history:', error);

    if (error instanceof PointsServiceError) {
      throw error;
    }

    if (isAxiosError(error)) {
      const message =
        error.response?.data?.error || error.message || 'Failed to fetch points history';
      throw new PointsServiceError(message, error);
    }

    throw new PointsServiceError(
      'An unexpected error occurred while fetching points history',
      error
    );
  }
};

export const getUserPointsById = async (
  userId: string
): Promise<PointsData & { username: string }> => {
  try {
    if (!userId || typeof userId !== 'string') {
      throw new PointsServiceError('Invalid user ID provided');
    }

    const response = await apiClient.get(`${API_URL}/user/${userId}`);

    if (!response.data || !response.data.data) {
      throw new PointsServiceError('Invalid response structure from server');
    }

    const validatedData = validatePointsDataResponse(response.data.data);

    return {
      ...validatedData,
      username:
        typeof response.data.data.username === 'string'
          ? response.data.data.username
          : 'Unknown User',
    };
  } catch (error) {
    console.error('Error fetching user points by ID:', error);

    if (error instanceof PointsServiceError) {
      throw error;
    }

    if (isAxiosError(error)) {
      const message = error.response?.data?.error || error.message || 'Failed to fetch user points';
      throw new PointsServiceError(message, error);
    }

    throw new PointsServiceError('An unexpected error occurred while fetching user points', error);
  }
};

export const calculateReputationScore = async (): Promise<PointsData> => {
  try {
    const response = await apiClient.post(`${API_URL}/calculate-reputation`);

    if (!response.data || !response.data.data) {
      throw new PointsServiceError('Invalid response structure from server');
    }

    return validatePointsDataResponse(response.data.data);
  } catch (error) {
    console.error('Error calculating reputation score:', error);

    if (error instanceof PointsServiceError) {
      throw error;
    }

    if (isAxiosError(error)) {
      const message =
        error.response?.data?.error || error.message || 'Failed to calculate reputation score';
      throw new PointsServiceError(message, error);
    }

    throw new PointsServiceError(
      'An unexpected error occurred while calculating reputation score',
      error
    );
  }
};
